import { EVENTS, sanitizePayload, correlationId } from "../../settings-api/diagnostic-schema.js";
import { readWidgetTokenClaims } from "./widget-credentials";

// Output selection belongs to a session. Never attach another call's player
// state to this call, or confuse live RTP with audible hardware playback.
export function playbackDiagnosticState(audio: HTMLMediaElement, selected: boolean): Record<string, any> {
  if (!selected) return { audioOutputSelected: false };
  try {
    const stream = audio.srcObject as MediaStream | null;
    const tracks = stream?.getAudioTracks?.() || [];
    return sanitizePayload({ audioOutputSelected: true, audioPlaybackAttached: Boolean(stream),
      audioPlaybackPaused: audio.paused, audioPlaybackMuted: audio.muted, audioPlaybackEnded: audio.ended,
      audioPlaybackReadyState: audio.readyState, audioPlaybackVolume: audio.volume,
      audioTrackCount: tracks.length, audioLiveTrackCount: tracks.filter(track => track.readyState === "live").length,
      audioMutedTrackCount: tracks.filter(track => track.muted).length,
      audioEndedTrackCount: tracks.filter(track => track.readyState === "ended").length });
  } catch { return { audioOutputSelected: true }; } // Observation cannot interrupt call cleanup.
}

// Snapshot only state and description presence; never inspect SDP/candidates.
export function peerDiagnosticState(peer?: RTCPeerConnection): Record<string, any> {
  if (!peer) return {};
  try {
    return { connectionState: peer.connectionState, iceConnectionState: peer.iceConnectionState,
      iceGatheringState: peer.iceGatheringState, signalingState: peer.signalingState,
      hasLocalDescription: Boolean(peer.localDescription), hasRemoteDescription: Boolean(peer.remoteDescription) };
  } catch { return {}; } // Diagnostics must not prevent timeout cleanup.
}

// Queue partition only, not authentication: the API verifies the actual JWT.
export function diagnosticScope(token: string, fallback: string): string {
  try {
    const claims = readWidgetTokenClaims(token);
    if (claims?.tenantId && claims.ext && claims.sub) return JSON.stringify([claims.tenantId, claims.ext, claims.sub]);
  } catch { /* Static or malformed token: do not carry queued events across a token change. */ }
  return JSON.stringify([fallback, token]);
}

export class DiagnosticQueue {
  private queue: any[] = [];
  private scope = "";
  private generation = 0;
  private credentialsGeneration = 0;
  private paused = false;
  private unsupported = false;
  private running = false;
  private timer: any;
  private retryMs = 5000;
  private dropped = 0;
  private lastHttpStatus = 0;
  constructor(private send: (events: any[], signal: AbortSignal) => Promise<{ status: number }>) {}
  configure(scope: string, credentialsChanged = false) {
    if (this.scope !== scope) {
      this.queue = []; this.generation++; this.scope = scope;
      this.dropped = 0; this.lastHttpStatus = 0;
    }
    if (credentialsChanged) {
      this.credentialsGeneration++;
      this.paused = false;
      this.retryMs = 5000;
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    this.schedule();
  }
  record(type: string, detail: Record<string, any> = {}) {
    if (!this.scope || this.unsupported || !EVENTS.has(type)) return;
    if (this.queue.length >= 200) { this.queue.shift(); this.dropped++; }
    this.queue.push({ eventId: crypto.randomUUID(), type, occurredAt: new Date().toISOString(),
      severity: detail.severity || (type.endsWith("_failed") || type === "error" ? "error" : "info"),
      callId: correlationId(detail.callId), linkedId: correlationId(detail.linkedId), payload: sanitizePayload(detail) });
    this.schedule();
  }
  private schedule() {
    if (this.timer || this.running || this.paused || this.unsupported || !this.queue.length) return;
    this.timer = setTimeout(() => { this.timer = undefined; void this.flush(); }, this.retryMs);
  }
  async flush() {
    if (this.running || this.paused || this.unsupported || !this.queue.length) return;
    clearTimeout(this.timer);
    this.timer = undefined;
    this.running = true;
    const generation = this.generation;
    const credentialsGeneration = this.credentialsGeneration;
    const previousLength = this.queue.length;
    this.queue = this.queue.filter((event) => Date.parse(event.occurredAt) > Date.now() - 300_000);
    this.dropped += previousLength - this.queue.length;
    const batch = this.queue.slice(0, 20);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      if (!batch.length) return;
      const response = await this.send(batch, controller.signal);
      if (generation !== this.generation) return;
      // A late rejection of the old token must not pause newly authenticated
      // requests. Keep same-identity events/IDs for retry, but accept an old 2xx.
      if ([401, 403].includes(response.status) && credentialsGeneration !== this.credentialsGeneration) return;
      this.lastHttpStatus = response.status;
      if ([401, 403].includes(response.status)) this.paused = true;
      else if ([404, 405].includes(response.status)) { this.unsupported = true; this.dropped += this.queue.length; this.queue = []; }
      else if ((response.status >= 200 && response.status < 300) || response.status === 400 || response.status === 413) {
        const ids = new Set(batch.map((event) => event.eventId));
        if (response.status === 400 || response.status === 413) this.dropped += batch.length;
        this.queue = this.queue.filter((event) => !ids.has(event.eventId));
        this.retryMs = 5000;
      } else this.retryMs = response.status === 429 ? 60_000 : Math.min(60_000, this.retryMs * 2);
    } catch { if (generation === this.generation) this.retryMs = Math.min(60_000, this.retryMs * 2); }
    finally { clearTimeout(timeout); this.running = false; this.schedule(); }
  }
  dispose() { clearTimeout(this.timer); this.timer = undefined; this.queue = []; this.generation++; this.scope = ""; }
  status() { return { pending: this.queue.length, paused: this.paused, unsupported: this.unsupported,
    dropped: this.dropped, lastHttpStatus: this.lastHttpStatus }; }
}

// Observe SIP.js delegates without replacing its transport or changing reconnects.
export class TransportDiagnostics {
  private connection?: { transportId: string; startedAt: number };
  constructor(private record: (type: string, payload: Record<string, any>) => void,
    private now: () => number = () => performance.now()) {}
  connected() {
    try {
      this.connection = { transportId: crypto.randomUUID(), startedAt: this.now() };
      this.record("transport_connected", { transportId: this.connection.transportId });
    } catch { /* Observation must never interrupt telephony. */ }
  }
  disconnected(error?: unknown) {
    const connection = this.connection;
    this.connection = undefined;
    if (!connection) return;
    try {
      const payload: Record<string, any> = { transportId: connection.transportId,
        transportUptimeMs: Math.max(0, Math.round(this.now() - connection.startedAt)),
        transportError: error ? "transport_error" : "none" };
      // SIP.js 0.21.2 exposes no structured CloseEvent in this delegate.
      // Extract only its numeric suffix; never retain the URL or free text.
      const match = error instanceof Error && error.message.length <= 4096 &&
        /^WebSocket closed wss?:\/\/[^\r\n]+ \(code: ([1-4]\d{3})\)$/.exec(error.message);
      if (match) { payload.wsCloseCode = Number(match[1]); payload.transportError = "websocket_closed"; }
      this.record("transport_disconnected", payload);
    } catch { /* Observation must never interrupt telephony. */ }
  }
}

export function audioStats(report: RTCStatsReport): Record<string, any> {
  const result: Record<string, any> = {};
  const entries: any[] = [];
  report.forEach((entry) => entries.push(entry));
  const sum = (key, value) => { if (typeof value === "number" && Number.isFinite(value)) result[key] = (result[key] || 0) + value; };
  const maximum = (key, value) => { if (typeof value === "number" && Number.isFinite(value)) result[key] = Math.max(result[key] || 0, value); };
  const inbound = entries.filter(entry => entry.type === "inbound-rtp" && (entry.kind || entry.mediaType) === "audio");
  result.inboundRtpStreams = inbound.length;
  // Keep quality counters from one SSRC only: mixed clocks/streams cannot form a meaningful ratio.
  if (inbound.length === 1) {
    const entry = inbound[0], codec = report.get(entry.codecId);
    for (const key of ["totalSamplesReceived", "silentConcealedSamples", "packetsDiscarded", "jitterBufferDelay",
      "jitterBufferTargetDelay", "jitterBufferEmittedCount", "insertedSamplesForDeceleration", "removedSamplesForAcceleration"])
      if (typeof entry[key] === "number" && Number.isFinite(entry[key])) result[key] = entry[key];
    result.inboundSsrc = entry.ssrc;
    result.inboundStatsTimestampMs = entry.timestamp;
    result.inboundCodec = typeof codec?.mimeType === "string" ? codec.mimeType.toLowerCase() : undefined;
    result.inboundCodecClockRate = codec?.clockRate;
    result.inboundCodecChannels = codec?.channels;
  }
  for (const entry of entries) {
    if ((entry.kind || entry.mediaType) !== "audio") continue;
    if (entry.type === "inbound-rtp") {
      for (const key of ["packetsReceived", "packetsLost", "bytesReceived", "concealedSamples", "concealmentEvents"]) sum(key, entry[key]);
      maximum("jitterMs", entry.jitter * 1000);
      maximum("audioLevel", entry.audioLevel);
    }
    if (entry.type === "outbound-rtp") { sum("packetsSent", entry.packetsSent); sum("bytesSent", entry.bytesSent); }
    if (entry.type === "remote-inbound-rtp") {
      sum("remotePacketsLost", entry.packetsLost);
      maximum("remoteJitterMs", entry.jitter * 1000);
      maximum("roundTripTimeMs", entry.roundTripTime * 1000);
      maximum("rtcpRoundTripTimeMs", entry.roundTripTime * 1000);
    }
  }
  const transport = entries.find((entry) => entry.type === "transport" && entry.selectedCandidatePairId);
  const pair = (transport && report.get(transport.selectedCandidatePairId)) ||
    entries.find((entry) => entry.type === "candidate-pair" && entry.nominated && entry.state === "succeeded");
  if (pair) {
    maximum("roundTripTimeMs", pair.currentRoundTripTime * 1000);
    maximum("iceRoundTripTimeMs", pair.currentRoundTripTime * 1000);
    maximum("availableOutgoingBitrate", pair.availableOutgoingBitrate);
    const local = report.get(pair.localCandidateId);
    result.localCandidateType = local?.candidateType;
    result.remoteCandidateType = report.get(pair.remoteCandidateId)?.candidateType;
    result.protocol = local?.protocol;
    result.relayProtocol = local?.relayProtocol;
  }
  return sanitizePayload(result);
}
